/**
 * JobPresets: the job's "Deliverables" card (admins; SafeShare slice 6.1).
 * The job's default deliverable presets, which every subject without its own
 * pick is processed into (a handheld subject may pick its own). Read through
 * getJobAutomation, saved through setJobAutomation {setting: "presets"}: the
 * server checks every preset, refuses a guest job and records the change.
 * Shown on the Rigs Settings tab (JobIngestSettings) and in the Capture
 * Arrays settings modal (JobSettingsModal).
 */
import React, { useEffect, useState } from "react";
import PresetPicker, { usePresets } from "../../../../components/PresetPicker.jsx";
import { C, font } from "../../../../lib/styles.js";
import { rigv2 } from "./rigv2Callables.js";

const same = (a, b) => JSON.stringify(a ?? []) === JSON.stringify(b ?? []);

export default function JobPresets({ jobId }) {
  const { presets, loading, error: loadError } = usePresets({});
  const [value, setValue] = useState([]);
  const [saved, setSaved] = useState(null);
  const [damaged, setDamaged] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const take = (r) => {
    setValue(r?.defaultPresetIds ?? []);
    setSaved(r?.defaultPresetIds ?? []);
    setDamaged(r?.defaultPresetsDamaged === true);
  };
  useEffect(() => {
    let live = true;
    setSaved(null);
    setError(null);
    rigv2.getJobAutomation({ jobId }).then((r) => { if (live) take(r); })
      .catch((e) => { if (live) setError(e?.message || "Could not load the job's deliverables."); });
    return () => { live = false; };
  }, [jobId]);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      take(await rigv2.setJobAutomation({ jobId, setting: "presets", presetIds: value }));
    } catch (e) {
      setError(e?.message || "Could not save.");
    } finally {
      setBusy(false);
    }
  };
  // A damaged stored default can always be saved over (the queue refuses it until then).
  const unchanged = same(value, saved) && !damaged;
  return (
    <section style={{ padding: 18, border: `1px solid ${C.border}`, borderRadius: 8, fontFamily: font, marginBottom: 16 }}>
      <h3 style={{ fontSize: 15, margin: "0 0 8px" }}>Deliverables</h3>
      <p style={{ fontSize: 12, color: C.muted }}>
        What this job&apos;s subjects are processed into and delivered as. A subject can pick its own.
      </p>
      {damaged && (
        <p role="alert" style={{ fontSize: 12, color: C.danger }}>
          The saved default is damaged, so nothing is processed from it: pick the presets again and save.
        </p>
      )}
      {loading ? <p>Loading…</p> : (
        <PresetPicker presets={presets} value={value} onChange={setValue}
          disabled={busy || saved === null || !!loadError} emptyLabel="None: processed as today" />
      )}
      {(error || loadError) && <p role="alert" style={{ color: C.danger }}>{error || loadError}</p>}
      <button type="button" className="hh-btn primary" onClick={save}
        disabled={busy || loading || !!loadError || saved === null || unchanged}>
        {busy ? "Saving…" : "Save"}
      </button>
    </section>
  );
}
